import {
  type ChatBrowsableRoom,
  type ChatClient,
  MAX_ROOM_NAME_LENGTH,
  MAX_TOPIC_LENGTH,
  normaliseTopic,
} from '@wtfalch/chat';
import { Button, Field, Input, Modal, Radio, RadioGroup } from '@wtfalch/design';
import { useEffect, useRef, useState } from 'react';

type Visibility = 'public' | 'private';

/** Room name, topic and who may join -- the second action in the room list's
 *  header. Resolves into the new room: `onCreated` gets its id. */
export function NewRoom({
  client,
  onClose,
  onCreated,
}: {
  client: ChatClient;
  onClose: () => void;
  onCreated: (roomId: string) => void;
}) {
  const [name, setName] = useState('');
  const [topic, setTopic] = useState('');
  const [visibility, setVisibility] = useState<Visibility>('public');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const running = useRef(false);

  const trimmed = name.trim();
  const create = async () => {
    if (running.current || trimmed === '') return;
    running.current = true;
    setBusy(true);
    setError(null);
    try {
      const cleanTopic = normaliseTopic(topic);
      const roomId = await client.createRoom({
        name: trimmed,
        ...(cleanTopic !== '' ? { topic: cleanTopic } : {}),
        visibility,
      });
      onCreated(roomId);
    } catch (e) {
      console.error('Could not create the room:', e);
      setError("Couldn't create the room. Nothing was made. Try again.");
      running.current = false;
      setBusy(false);
    }
  };

  return (
    <Modal
      title="New room"
      onClose={onClose}
      closeDisabled={busy}
      dismissOnScrim={false}
      footer={
        <>
          <Button kind="ghost" onPress={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button busy={busy} disabled={trimmed === '' || busy} onPress={() => void create()}>
            Create room
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Name" required>
          {(field) => (
            <Input
              block
              {...field}
              value={name}
              maxLength={MAX_ROOM_NAME_LENGTH}
              onChange={(e) => setName(e.target.value)}
            />
          )}
        </Field>
        <Field label="Topic" hint="Optional. What the room is for.">
          {(field) => (
            <Input
              block
              {...field}
              value={topic}
              maxLength={MAX_TOPIC_LENGTH}
              onChange={(e) => setTopic(e.target.value)}
            />
          )}
        </Field>
        <RadioGroup
          label="Who can join"
          value={visibility}
          onChange={(v) => setVisibility(v === 'private' ? 'private' : 'public')}
        >
          <Radio value="public">Public to the company</Radio>
          <Radio value="private">Private, by invitation</Radio>
        </RadioGroup>
        <p className="text-xs text-bad m-0" role="alert" style={{ minHeight: '1.25em' }}>
          {error}
        </p>
      </div>
    </Modal>
  );
}

/** The company's rooms this person is not in yet, each with a Join. */
export function BrowseRooms({ client, onClose }: { client: ChatClient; onClose: () => void }) {
  const [rooms, setRooms] = useState<readonly ChatBrowsableRoom[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [joined, setJoined] = useState<ReadonlySet<string>>(new Set());
  const [joining, setJoining] = useState<string | null>(null);
  const [joinError, setJoinError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    client.browseRooms().then(
      (list) => {
        if (live) setRooms(list);
      },
      (e: unknown) => {
        console.error('Could not list the company rooms:', e);
        if (live) setLoadError(true);
      },
    );
    return () => {
      live = false;
    };
  }, [client]);

  const join = async (room: ChatBrowsableRoom) => {
    if (joining) return;
    setJoining(room.id);
    setJoinError(null);
    try {
      await client.joinBrowsableRoom(room.id);
      setJoined((prev) => new Set(prev).add(room.id));
    } catch (e) {
      console.error('Could not join the room:', e);
      setJoinError(`Couldn't join ${room.name}. Try again.`);
    } finally {
      setJoining(null);
    }
  };

  return (
    <Modal
      title="Browse rooms"
      description="Rooms in your company that you have not joined."
      onClose={onClose}
      footer={
        <Button kind="ghost" onPress={onClose}>
          Close
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        {loadError ? (
          <p className="text-sm text-bad m-0" role="alert">
            Couldn't list the company's rooms. Try again later.
          </p>
        ) : rooms === null ? (
          <p className="text-sm text-muted m-0">Loading…</p>
        ) : rooms.length === 0 ? (
          <p className="text-sm text-muted m-0">There are no more rooms to join.</p>
        ) : (
          <ul className="flex flex-col gap-3 m-0 p-0" style={{ listStyle: 'none' }}>
            {rooms.map((room) => (
              <li key={room.id} className="flex items-center gap-3">
                <span className="flex flex-col grow min-w-0">
                  <span className="text-sm font-strong truncate">{room.name}</span>
                  {room.topic && <span className="text-xs text-muted truncate">{room.topic}</span>}
                  <span className="text-xs text-muted">
                    {room.memberCount} member{room.memberCount === 1 ? '' : 's'}
                  </span>
                </span>
                {joined.has(room.id) ? (
                  <span className="text-xs text-muted flex-none">Joined</span>
                ) : (
                  <Button
                    size="sm"
                    aria-label={`Join ${room.name}`}
                    disabled={joining !== null}
                    busy={joining === room.id}
                    onPress={() => void join(room)}
                  >
                    Join
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
        <p className="text-xs text-bad m-0" role="alert" style={{ minHeight: '1.25em' }}>
          {joinError}
        </p>
      </div>
    </Modal>
  );
}
